import type { Locale } from '@iac/contracts';
import { useState } from 'react';
import type { Dictionary } from '../i18n/dictionary';
import type { Story } from '../lib/story';
import { SegmentedControl } from './SegmentedControl';

type Range = '30' | '90' | '365' | 'all';

const sample: Dictionary<{
  label: string;
  ranges: Record<Range, string>;
  viewLabel: string;
  views: Record<'cards' | 'chart', string>;
}> = {
  pl: {
    label: 'Zakres',
    ranges: { '30': '30 dni', '90': '90 dni', '365': 'Rok', all: 'Wszystko' },
    viewLabel: 'Widok',
    views: { cards: 'Karty', chart: 'Wykres' },
  },
  en: {
    label: 'Range',
    ranges: { '30': '30 days', '90': '90 days', '365': 'Year', all: 'All' },
    viewLabel: 'View',
    views: { cards: 'Cards', chart: 'Chart' },
  },
};

/** The control owns no state; the screen around it does. */
function Demo<T extends string>({
  label,
  labels,
  initial,
}: {
  label: string;
  labels: Record<T, string>;
  initial: T;
}) {
  const [value, setValue] = useState(initial);
  const options = (Object.keys(labels) as T[]).map((key) => ({
    value: key,
    label: labels[key],
  }));
  return (
    <SegmentedControl
      label={label}
      options={options}
      value={value}
      onChange={setValue}
    />
  );
}

const ranges = (locale: Locale) => (
  <Demo<Range>
    label={sample[locale].label}
    labels={sample[locale].ranges}
    initial="90"
  />
);

export default {
  kind: 'component',
  title: { pl: 'Przełącznik segmentowy', en: 'Segmented control' },
  description: {
    pl: 'Kilka zawsze widocznych opcji, z których wybrana jest dokładnie jedna — do przełączania, jak ekran pokazuje treść. Stan trzyma ekran; komponent zgłasza tylko nowy wybór. To `radiogroup` z jednym przystankiem tabulatora: strzałki przesuwają wybór. Na wąskim ekranie opcje wypełniają całą szerokość.',
    en: 'A few always-visible options with exactly one selected — for switching how a screen shows its content. The screen owns the value; the component only reports the next one. It is a `radiogroup` with one tab stop: the arrow keys move the selection. On a narrow screen the options fill the full width.',
  },
  variants: [
    {
      name: { pl: 'Cztery zakresy', en: 'Four ranges' },
      render: ranges,
    },
    {
      name: { pl: 'Dwie opcje', en: 'Two options' },
      render: (locale) => (
        <Demo
          label={sample[locale].viewLabel}
          labels={sample[locale].views}
          initial="chart"
        />
      ),
    },
  ],
} satisfies Story;
